import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '@environments/environment';
import { ActiveContextService } from '@shared/services/active-context.service';
import { AuthService } from '@shared/services/auth.service';

/**
 * Permisos del usuario en la sucursal activa.
 *
 * Un usuario de negocio no tiene permisos globales: viven en la asignación de cada
 * sucursal, así que se piden al gateway (`auth/me/context`) y se vuelven a pedir al
 * cambiar de negocio o sucursal. Esto controla lo que se muestra, no lo que se puede
 * hacer: la validación real es del gateway.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  #http = inject(HttpClient);
  #context = inject(ActiveContextService);
  #auth = inject(AuthService);

  readonly permissions = signal<string[]>([]);
  /** true cuando ya hay permisos cargados para el contexto activo. */
  readonly loaded = signal(false);

  #loadedKey: string | null = null;
  #failed = false;
  #failedKey: string | null = null;
  #failedAt = 0;
  #pending: Promise<void> | null = null;

  constructor() {
    // Refresca al cambiar de sucursal/negocio o al iniciar/cerrar sesión.
    effect(() => {
      const key = this.#currentKey();
      untracked(() => {
        if (!this.#auth.currentUser()) this.clear();
        else if (key !== this.#loadedKey) void this.sync();
      });
    });
  }

  #currentKey(): string {
    const user = this.#auth.currentUser();
    const uid = user?.id || user?._id || '';
    return `${uid}|${this.#context.activeBusiness()?._id ?? ''}|${this.#context.activeEstablishment()?._id ?? ''}`;
  }

  /** Garantiza que los permisos correspondan al contexto activo (lo usan los guards). */
  sync(): Promise<void> {
    const key = this.#currentKey();
    if (this.#pending) return this.#pending.then(() => (key === this.#loadedKey ? undefined : this.sync()));
    if (key === this.#loadedKey && this.loaded()) return Promise.resolve();
    // Si hace poco falló para este mismo contexto, no se vuelve a esperar al gateway en cada clic del menú (cada intento costaba hasta el tope de espera).
    if (this.#failed && this.#failedKey === key && Date.now() - this.#failedAt < 30_000) return Promise.resolve();

    this.loaded.set(false);
    this.#failed = false;
    // Con tope de tiempo: si el gateway tarda o se está reiniciando (p. ej. por un despliegue), la navegación no puede quedar colgada esperándolo.
    this.#pending = firstValueFrom(
      this.#http.get<{ permissions?: string[] }>(`${environment.apiUrl}/auth/me/context`).pipe(timeout(5000)),
    )
      .then(res => this.permissions.set(res?.permissions ?? []))
      // Un fallo de red NO deja al usuario sin permisos: conserva los que ya tenía y se vuelve a pedir en la próxima navegación.
      .catch(() => { this.#failed = true; this.#failedKey = key; this.#failedAt = Date.now(); })
      .finally(() => {
        this.#loadedKey = this.#failed ? null : key;
        this.loaded.set(true);
        this.#pending = null;
      });
    return this.#pending;
  }

  /** La última consulta de permisos falló (red o gateway caído): no hay base para negar el acceso. */
  get syncFailed(): boolean {
    return this.#failed;
  }

  clear(): void {
    this.permissions.set([]);
    this.loaded.set(false);
    this.#loadedKey = null;
  }

  /** El comodín '*' concede todo. */
  can(key: string): boolean {
    const granted = this.permissions();
    return granted.includes('*') || granted.includes(key);
  }

  /** Basta con tener uno; lista vacía o ausente = sin restricción. */
  canAny(keys?: string[]): boolean {
    return !keys?.length || keys.some(k => this.can(k));
  }
}
